// offline.ts — cola de sincronización offline para el módulo Restaurante.
//
// ALCANCE (ver README): la cola cubre acciones sobre una comanda de mesa —
// tanto abrir una mesa NUEVA sin conexión como agregar/quitar ítems, cambiar
// el estado de cocina, enviar a cocina y cerrar/cobrar. Crear mesas/salones/
// ingredientes/recetas nuevos, y anular una comanda ya existente en el
// servidor, siguen requiriendo conexión real (anular revierte inventario y
// estado de mesa: no es seguro dejarlo en cola si puede chocar con otras
// acciones ya encoladas sobre la misma comanda).
//
// Cómo funciona abrir una mesa sin conexión: como el servidor todavía no le
// asignó un ID real a la comanda, el navegador le da uno TEMPORAL (un número
// negativo, ver `nuevoIdTemporal`) y lo usa mientras tanto para que el
// mesero pueda seguir agregando ítems, enviarla a cocina, etc. normalmente.
// Cada acción posterior sobre esa comanda queda marcada con
// `refTempComandaId` apuntando a ese ID temporal. Al reconectar, la cola se
// sincroniza en el mismo orden en que se creó (FIFO) — así "abrir la mesa"
// siempre se envía antes que sus ítems — y en cuanto el servidor responde
// con el ID real, `sincronizar()` reemplaza ese ID temporal por el real en
// el resto de las acciones encoladas antes de enviarlas. Si "abrir la mesa"
// termina rechazada por el servidor (por ejemplo, la misma mesa ya fue
// abierta desde otro dispositivo mientras ambos estaban sin conexión), todas
// las acciones que dependían de ese ID temporal se descartan en cascada y se
// reportan como error — no se reintentan para siempre ni se aplican a una
// comanda que nunca llegó a existir.
//
// Límite que SÍ queda, con honestidad: todo esto vive en el estado de React
// de la pestaña abierta. Si el mesero recarga la página (o cierra la
// pestaña) mientras hay una mesa recién abierta sin conexión y TODAVÍA sin
// sincronizar, esa mesa vuelve a verse "libre" en pantalla hasta que la cola
// logre sincronizar en segundo plano — la cola en sí no se pierde (vive en
// IndexedDB), pero la vista optimista de esa mesa sí, porque no es una
// comanda ya conocida por el servidor que se pueda recuperar de la caché de
// lectura (ver `leerCacheComanda`). En la práctica esto solo importa si
// además de perder internet se recarga la página antes de reconectar —
// seguir usando la misma pestaña, que es el caso normal, no tiene este
// problema.
//
// Cómo se distingue "no hay red" de "el servidor rechazó la petición":
// api.ts lanza ApiError cuando el servidor SÍ respondió (4xx/5xx) — eso
// nunca se encola, porque encolarlo solo pospondría un error que ya sabemos
// que va a volver a pasar (p. ej. "faltan comensales"), y el usuario debe
// verlo y corregirlo ahora. Cuando en cambio fetch() ni siquiera logra
// contactar al servidor (typeof error es TypeError, p.ej. "Failed to
// fetch"), eso SÍ se trata como pérdida de conexión y se encola.
import { api, ApiError } from "./api";

// --------------------------------------------------------------- IndexedDB
const DB_NOMBRE = "sigepro_restaurante_offline";
const DB_VERSION = 3;
const STORE_COLA = "cola";
const STORE_CACHE = "cache_comandas";
const STORE_CACHE_PRODUCTOS = "cache_productos"; // legado (v2) — ya no se escribe, solo se deja de crear si falta
const STORE_CACHE_LISTAS = "cache_listas"; // genérico: productos, turnos abiertos, clientes, meseros...

function abrirDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NOMBRE, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_COLA)) {
        db.createObjectStore(STORE_COLA, { keyPath: "id", autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        db.createObjectStore(STORE_CACHE, { keyPath: "comandaId" });
      }
      if (!db.objectStoreNames.contains(STORE_CACHE_PRODUCTOS)) {
        db.createObjectStore(STORE_CACHE_PRODUCTOS, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORE_CACHE_LISTAS)) {
        db.createObjectStore(STORE_CACHE_LISTAS, { keyPath: "clave" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// -------------------------------------------------------------------- tipos
export type Metodo = "POST" | "PUT" | "PATCH" | "DELETE";

export type AccionCola = {
  id?: number;
  metodo: Metodo;
  path: string; // relativo a /api, ej. "/restaurante/comandas/5/items" — si depende
  // de una comanda todavía sin sincronizar, el segmento numérico es el ID
  // temporal (negativo) hasta que `refTempComandaId` se resuelva.
  body?: any;
  descripcion: string; // texto legible para el usuario, ej. "2× Hamburguesa — Mesa 5"
  comandaId: number; // ID real, o temporal (negativo) mientras no sincronice
  creadoEn: number;
  intentos: number;
  ultimoError?: string;
  // Presente SOLO en la acción "abrir comanda": el ID temporal que esta
  // acción resuelve una vez que el servidor responda con el ID real.
  tempComandaId?: number;
  // Presente en cualquier acción posterior sobre una comanda que se abrió
  // sin conexión: qué ID temporal debe resolverse (vía `tempComandaId` de
  // OTRA acción ya procesada) antes de poder enviar esta.
  refTempComandaId?: number;
};

// Las comandas abiertas sin conexión reciben un ID temporal NEGATIVO — nunca
// puede chocar con un ID real de la base de datos (autoincremental, siempre
// positivo), así que basta con el signo para distinguir "todavía no existe
// en el servidor" de un ID real.
let contadorTemp = 0;
export function nuevoIdTemporal(): number {
  contadorTemp += 1;
  return -(Date.now() * 1000 + contadorTemp);
}
export function esIdTemporal(id: number): boolean {
  return id < 0;
}

// ---------------------------------------------------------- acceso a la cola
async function encolar(accion: Omit<AccionCola, "id" | "creadoEn" | "intentos">): Promise<AccionCola> {
  const db = await abrirDB();
  const registro: AccionCola = { ...accion, creadoEn: Date.now(), intentos: 0 };
  const guardado = await new Promise<AccionCola>((resolve, reject) => {
    const tx = db.transaction(STORE_COLA, "readwrite");
    const req = tx.objectStore(STORE_COLA).add(registro);
    req.onsuccess = () => resolve({ ...registro, id: req.result as number });
    req.onerror = () => reject(req.error);
  });
  db.close();
  notificarCambio();
  // Intenta sincronizar de inmediato por si la desconexión ya se resolvió
  // (p.ej. un solo request falló por una intermitencia puntual).
  sincronizar();
  return guardado;
}

export async function listarCola(): Promise<AccionCola[]> {
  const db = await abrirDB();
  const items = await new Promise<AccionCola[]>((resolve, reject) => {
    const tx = db.transaction(STORE_COLA, "readonly");
    const req = tx.objectStore(STORE_COLA).getAll();
    req.onsuccess = () => resolve(req.result as AccionCola[]);
    req.onerror = () => reject(req.error);
  });
  db.close();
  // getAll() en IndexedDB ya devuelve en orden ascendente de key (= orden de
  // inserción con autoIncrement), lo que garantiza FIFO al sincronizar.
  return items;
}

export async function listarColaDeComanda(comandaId: number): Promise<AccionCola[]> {
  return (await listarCola()).filter((a) => a.comandaId === comandaId);
}

async function quitarDeCola(id: number): Promise<void> {
  const db = await abrirDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_COLA, "readwrite");
    tx.objectStore(STORE_COLA).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
  notificarCambio();
}

// Descarta por completo una comanda que se abrió sin conexión y que TODAVÍA
// no sincronizó: quita de la cola tanto la acción "abrir comanda" como
// cualquier acción posterior que dependiera de su ID temporal (agregar
// ítems, enviar a cocina, etc.). Es la única forma de "anular" una comanda
// que nunca llegó a existir en el servidor — no tiene sentido mandar un
// anular al servidor para algo que el servidor no conoce.
export async function descartarComandaTemporal(tempId: number): Promise<void> {
  const cola = await listarCola();
  const aQuitar = cola.filter((a) => a.tempComandaId === tempId || a.refTempComandaId === tempId);
  for (const a of aQuitar) {
    await quitarDeCola(a.id!);
  }
}

async function marcarIntentoFallido(accion: AccionCola, error: string): Promise<void> {
  const db = await abrirDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_COLA, "readwrite");
    tx.objectStore(STORE_COLA).put({ ...accion, intentos: accion.intentos + 1, ultimoError: error });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
  notificarCambio();
}

// --------------------------------------------------- caché de última lectura
// Guarda la última respuesta buena de GET /restaurante/comandas/:id para que,
// si se pierde la conexión y hay que recargar la pantalla, el mesero siga
// viendo los ítems que ya tenía la comanda (aunque ya no pueda refrescarlos
// hasta reconectar).
export async function guardarCacheComanda(comanda: any): Promise<void> {
  if (!comanda || !comanda.id) return;
  const db = await abrirDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_CACHE, "readwrite");
    tx.objectStore(STORE_CACHE).put({ comandaId: comanda.id, comanda, guardadoEn: Date.now() });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function leerCacheComanda(comandaId: number): Promise<any | null> {
  const db = await abrirDB();
  const registro = await new Promise<any>((resolve, reject) => {
    const tx = db.transaction(STORE_CACHE, "readonly");
    const req = tx.objectStore(STORE_CACHE).get(comandaId);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return registro ? registro.comanda : null;
}

// Caché genérica de listas de catálogo (productos disponibles, turnos de
// caja abiertos, clientes, meseros) — sin esto, aunque la comanda en sí se
// pueda seguir viendo/editando offline gracias a `cache_comandas`, los
// formularios que dependen de estas listas (agregar ítem, cerrar y cobrar,
// sentar una mesa con mesero) quedarían con los selectores vacíos justo
// cuando más se necesitan. Cada lista se cachea sola cuando su GET tiene
// éxito, y se usa como respaldo cuando ese mismo GET falla por red.
async function guardarCacheLista(clave: string, datos: any[]): Promise<void> {
  const db = await abrirDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_CACHE_LISTAS, "readwrite");
    tx.objectStore(STORE_CACHE_LISTAS).put({ clave, datos, guardadoEn: Date.now() });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

async function leerCacheLista(clave: string): Promise<any[]> {
  const db = await abrirDB();
  const registro = await new Promise<any>((resolve, reject) => {
    const tx = db.transaction(STORE_CACHE_LISTAS, "readonly");
    const req = tx.objectStore(STORE_CACHE_LISTAS).get(clave);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return registro ? registro.datos : [];
}

export const guardarCacheProductos = (productos: any[]) => guardarCacheLista("productos", productos);
export const leerCacheProductos = () => leerCacheLista("productos");
export const guardarCacheTurnos = (turnos: any[]) => guardarCacheLista("turnos_abiertos", turnos);
export const leerCacheTurnos = () => leerCacheLista("turnos_abiertos");
export const guardarCacheClientes = (clientes: any[]) => guardarCacheLista("clientes", clientes);
export const leerCacheClientes = () => leerCacheLista("clientes");
export const guardarCacheMeseros = (meseros: any[]) => guardarCacheLista("meseros", meseros);
export const leerCacheMeseros = () => leerCacheLista("meseros");

// --------------------------------------------------------------- pub/sub UI
type Escucha = (pendientes: AccionCola[]) => void;
const escuchas = new Set<Escucha>();

function notificarCambio() {
  listarCola().then((items) => escuchas.forEach((cb) => cb(items)));
}

export function suscribirCola(cb: Escucha): () => void {
  escuchas.add(cb);
  listarCola().then(cb); // entrega el estado actual de inmediato
  return () => escuchas.delete(cb);
}

type EscuchaSync = () => void;
const escuchasSync = new Set<EscuchaSync>();
export function suscribirSincronizacion(cb: EscuchaSync): () => void {
  escuchasSync.add(cb);
  return () => escuchasSync.delete(cb);
}

// -------------------------------------------------------- ejecutar o encolar
function esFalloDeRed(e: unknown): boolean {
  // ApiError = el servidor respondió (con un error de negocio/validación,
  // 401, etc.) — eso NUNCA se trata como desconexión.
  return !(e instanceof ApiError);
}

async function llamar(metodo: Metodo, path: string, body?: any): Promise<any> {
  switch (metodo) {
    case "POST": return api.post(path, body);
    case "PUT": return api.put(path, body);
    case "PATCH": return api.patch(path, body);
    case "DELETE": return api.del(path);
  }
}

export type ResultadoAccion<T = any> =
  | { offline: false; data: T }
  | { offline: true; encolado: AccionCola };

async function ejecutarOEncolar<T = any>(
  metodo: Metodo, path: string, body: any, descripcion: string, comandaId: number,
): Promise<ResultadoAccion<T>> {
  if (esIdTemporal(comandaId)) {
    // La comanda en sí todavía no existe en el servidor (se abrió sin
    // conexión) — no tiene sentido intentar esta llamada de verdad, porque
    // el servidor respondería con un 404 real (ApiError) en vez de un fallo
    // de red, y eso rompería la cola en vez de encolarse. Se encola directo,
    // marcada para que `sincronizar()` reemplace el ID temporal por el real
    // en cuanto "abrir comanda" haya sincronizado.
    const encolado = await encolar({ metodo, path, body, descripcion, comandaId, refTempComandaId: comandaId });
    return { offline: true, encolado };
  }
  try {
    const data = await llamar(metodo, path, body);
    return { offline: false, data };
  } catch (e) {
    if (!esFalloDeRed(e)) throw e; // error real del servidor: nunca se silencia
    const encolado = await encolar({ metodo, path, body, descripcion, comandaId });
    return { offline: true, encolado };
  }
}

// Envoltorios específicos para las acciones dentro del alcance offline.
// Restaurante.tsx los usa en vez de llamar a `api` directamente para estas
// operaciones, y aplica su propia actualización optimista en pantalla cuando
// `offline: true` (el módulo de cola no conoce la forma de los datos de la
// comanda, así que esa parte queda del lado del componente).
export const agregarItemOffline = (comandaId: number, body: any, descripcion: string) =>
  ejecutarOEncolar("POST", `/restaurante/comandas/${comandaId}/items`, body, descripcion, comandaId);

export const quitarItemOffline = (comandaId: number, itemId: number, descripcion: string) =>
  ejecutarOEncolar("DELETE", `/restaurante/comandas/${comandaId}/items/${itemId}`, undefined, descripcion, comandaId);

export const cambiarEstadoItemOffline = (comandaId: number, itemId: number, estado: string, descripcion: string) =>
  ejecutarOEncolar("PATCH", `/restaurante/comandas/${comandaId}/items/${itemId}/estado`, { estado }, descripcion, comandaId);

export const enviarACocinaOffline = (comandaId: number, descripcion: string) =>
  ejecutarOEncolar("POST", `/restaurante/comandas/${comandaId}/enviar-cocina`, undefined, descripcion, comandaId);

export const reenviarACocinaOffline = (comandaId: number, descripcion: string) =>
  ejecutarOEncolar("POST", `/restaurante/comandas/${comandaId}/reenviar-cocina`, undefined, descripcion, comandaId);

export const cerrarComandaOffline = (comandaId: number, body: any, descripcion: string) =>
  ejecutarOEncolar("POST", `/restaurante/comandas/${comandaId}/cerrar`, body, descripcion, comandaId);

export const moverCuentaOffline = (comandaId: number, itemId: number, cuenta: number, descripcion: string) =>
  ejecutarOEncolar("PATCH", `/restaurante/comandas/${comandaId}/items/${itemId}/cuenta`, { cuenta }, descripcion, comandaId);

export const cobrarCuentaOffline = (comandaId: number, cuenta: number, body: any, descripcion: string) =>
  ejecutarOEncolar("POST", `/restaurante/comandas/${comandaId}/cuentas/${cuenta}/cobrar`, body, descripcion, comandaId);

// Abrir una mesa (comanda nueva) — a diferencia de las anteriores, si falla
// por red no se puede simplemente "encolar la misma llamada": todavía no
// hay ningún ID de comanda al que referirse. Se genera uno temporal para que
// el resto de la UI (y las acciones de arriba) puedan seguir operando como
// si la comanda ya existiera.
export type ResultadoAbrirComanda =
  | { offline: false; data: any }
  | { offline: true; tempId: number };

export async function abrirComandaOffline(mesaId: number, body: any, descripcion: string): Promise<ResultadoAbrirComanda> {
  const cuerpo = { ...body, mesaId };
  try {
    const data = await api.post("/restaurante/comandas", cuerpo);
    return { offline: false, data };
  } catch (e) {
    if (!esFalloDeRed(e)) throw e;
    const tempId = nuevoIdTemporal();
    await encolar({ metodo: "POST", path: "/restaurante/comandas", body: cuerpo, descripcion, comandaId: tempId, tempComandaId: tempId });
    return { offline: true, tempId };
  }
}

// ------------------------------------------------------------ sincronizador
let sincronizando = false;

export async function sincronizar(): Promise<{ ok: number; error: number }> {
  if (sincronizando) return { ok: 0, error: 0 };
  if (typeof navigator !== "undefined" && navigator.onLine === false) return { ok: 0, error: 0 };
  sincronizando = true;
  let ok = 0;
  let error = 0;
  // Resoluciones de ID temporal -> ID real, y temporales cuya comanda nunca
  // llegó a crearse en el servidor. Viven solo durante esta pasada: como la
  // cola es estrictamente FIFO, la acción "abrir comanda" de un ID temporal
  // siempre se procesa (y por lo tanto se resuelve o se marca como fallida)
  // ANTES que cualquier acción que dependa de ella, así que no hace falta
  // persistir esto entre llamadas a `sincronizar()`.
  const resoluciones = new Map<number, number>();
  const fallidos = new Set<number>();
  try {
    // Se procesa en orden FIFO y se DETIENE ante la primera acción que
    // vuelva a fallar por falta de red — si esa falló, las siguientes de la
    // misma comanda muy probablemente también, y reordenar la cola (saltarse
    // una y ejecutar la siguiente) podría aplicar los cambios fuera de
    // orden (p.ej. cobrar antes de que se hayan agregado todos los ítems).
    for (;;) {
      const cola = await listarCola();
      if (cola.length === 0) break;
      const accion = cola[0];

      if (accion.refTempComandaId != null) {
        if (fallidos.has(accion.refTempComandaId)) {
          // La comanda de la que depende esta acción nunca llegó a crearse
          // en el servidor (su "abrir comanda" ya se descartó más arriba en
          // esta misma pasada) — no hay a qué ID real aplicar esto. Se
          // descarta en cascada y se reporta como error, no se reintenta.
          await quitarDeCola(accion.id!);
          error++;
          continue;
        }
        const real = resoluciones.get(accion.refTempComandaId);
        if (real == null) {
          // No debería pasar (FIFO garantiza que "abrir comanda" va antes),
          // pero por seguridad se corta aquí en vez de arriesgarse a mandar
          // un ID temporal al servidor.
          break;
        }
        accion.path = accion.path.replace(String(accion.refTempComandaId), String(real));
        // Se limpia de una vez: si el envío de abajo falla por red y esta
        // acción queda persistida de nuevo para reintentar más tarde
        // (`marcarIntentoFallido`), su `path` ya guarda el ID real. Si no se
        // limpiara aquí, la próxima pasada de `sincronizar()` volvería a
        // exigir una resolución que ya no existe (el mapa de resoluciones es
        // solo de esta pasada) y la acción quedaría atascada para siempre.
        accion.refTempComandaId = undefined;
      }

      try {
        const resultado = await llamar(accion.metodo, accion.path, accion.body);
        if (accion.tempComandaId != null && resultado && resultado.id != null) {
          resoluciones.set(accion.tempComandaId, resultado.id);
        }
        await quitarDeCola(accion.id!);
        ok++;
      } catch (e) {
        if (e instanceof ApiError) {
          // El servidor por fin respondió, pero rechazó la acción encolada
          // (p.ej. ya no hay stock, la mesa ya fue sentada desde otro
          // dispositivo, la comanda ya se cerró). No tiene sentido
          // reintentarla eternamente: se saca de la cola y se reporta como
          // error para que el usuario la revise manualmente, y se sigue con
          // el resto de la cola.
          if (accion.tempComandaId != null) fallidos.add(accion.tempComandaId);
          await quitarDeCola(accion.id!);
          error++;
          continue;
        }
        // Sigue sin haber red: se detiene el barrido completo y se reintenta
        // más adelante (evento "online" o el intervalo de respaldo).
        await marcarIntentoFallido(accion, String((e as any)?.message || e));
        break;
      }
    }
  } finally {
    sincronizando = false;
  }
  if (ok > 0) escuchasSync.forEach((cb) => cb());
  return { ok, error };
}

export async function contarPendientes(): Promise<number> {
  return (await listarCola()).length;
}

// Arranque automático: al reconectar (evento "online") y, como respaldo,
// cada 20s — el evento "online" del navegador no siempre es confiable
// (algunas redes reportan "conectado" aunque no haya salida real a
// internet), así que el intervalo periódico es la garantía real de que la
// cola no quede atascada indefinidamente.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => sincronizar());
  setInterval(() => sincronizar(), 20000);
  // Intento inicial al cargar la página, por si quedaron acciones
  // pendientes de una sesión anterior que se cerró sin conexión.
  sincronizar();
}
